<template>
  <div class="quick-nav-widget">
    <v-row dense>
      <v-col
        v-for="item in accessibleNavItems"
        :key="item.label"
        cols="6"
      >
        <div
          class="nav-tile d-flex align-center pa-3"
          :style="{ background: tileBg(item) }"
          @click="navigate(item.route)"
        >
          <v-avatar :color="item.color" size="36" class="mr-3 nav-avatar">
            <v-icon dark size="18">{{ item.icon }}</v-icon>
          </v-avatar>
          <span class="text-body-2 font-weight-medium nav-label">
            {{ item.label }}
          </span>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import {
  mdiTextSearch,
  mdiAccountCashOutline,
  mdiReceipt,
  mdiBookOpen,
  mdiEqualBox,
  mdiArrowDecision,
  mdiBank,
  mdiBankTransfer,
} from '@mdi/js';

// #rrggbb -> rgba() at the given alpha, used to wash a tile in its own accent on the dark theme.
function hexToRgba(hex, alpha) {
  const r = parseInt(hex.substring(1, 3), 16);
  const g = parseInt(hex.substring(3, 5), 16);
  const b = parseInt(hex.substring(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export default {
  name: 'QuickNavWidget',
  computed: {
    ...mapGetters('auth/token', [
      'isTaxAccessible',
      'isPaymentsAccessible',
      'isMiscAccessible',
      'isGlAccessible',
      'isWarAccessible',
      'isApportionmentAccessible',
      'isMtgAccessible',
      'isODAccessible',
    ]),
    accessibleNavItems() {
      return [
        {
          label: 'Tax Inquiry',
          icon: mdiTextSearch,
          route: '/tax/taxinquiry/taxrollsearch',
          color: '#43a047',
          bg: '#f1f8f1',
          accessible: this.isTaxAccessible,
        },
        {
          label: 'Payments',
          icon: mdiAccountCashOutline,
          route: '/tax/payments/dashboard',
          color: '#1e88e5',
          bg: '#e8f0fe',
          accessible: this.isPaymentsAccessible,
        },
        {
          label: 'Misc Receipts',
          icon: mdiReceipt,
          route: '/misc/transactions',
          color: '#8e24aa',
          bg: '#f3e5f5',
          accessible: this.isMiscAccessible,
        },
        {
          label: 'General Ledger',
          icon: mdiBookOpen,
          route: '/gl/transactions',
          color: '#00897b',
          bg: '#e0f2f1',
          accessible: this.isGlAccessible,
        },
        {
          label: 'Warrants',
          icon: mdiEqualBox,
          route: '/war/transactions',
          color: '#f4511e',
          bg: '#fbe9e7',
          accessible: this.isWarAccessible,
        },
        {
          label: 'Apportion',
          icon: mdiArrowDecision,
          route: '/apportionment/taxes',
          color: '#f9a825',
          bg: '#fffde7',
          accessible: this.isApportionmentAccessible,
        },
        {
          label: 'Mortgage Tax',
          icon: mdiBank,
          route: '/mtg/transactions',
          color: '#5d4037',
          bg: '#efebe9',
          accessible: this.isMtgAccessible,
        },
        {
          label: 'Official Depository',
          icon: mdiBankTransfer,
          route: '/od/transactions',
          color: '#0277bd',
          bg: '#e1f5fe',
          accessible: this.isODAccessible,
        },
      ].filter((item) => item.accessible);
    },
  },
  methods: {
    /* The pastel `bg` on each item is an opaque light-theme fill - on a #1e1e1e card it reads as
       eight bright blocks. Wash the tile in its own avatar colour instead, so the tiles stay
       colour-coded (which is the point of them) without lighting up the card. */
    tileBg(item) {
      return this.$vuetify.theme.dark ? hexToRgba(item.color, 0.16) : item.bg;
    },
    navigate(route) {
      const resolved = this.$router.resolve(route);
      window.open(resolved.href, '_blank', 'noopener,noreferrer');
    },
  },
};
</script>

<style scoped>
.nav-tile {
  border-radius: 12px;
  cursor: pointer;
  overflow: hidden;
  transition: all 0.2s ease;
  border: 1px solid transparent;
  min-height: 60px;
}

.nav-tile:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
  border-color: var(--widget-border, rgba(0, 0, 0, 0.06));
}

.nav-tile:active {
  transform: translateY(0);
}

.nav-avatar {
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.nav-label {
  color: var(--widget-body-text, #3c4043);
  font-size: 11.5px;
  line-height: 1.3;
  overflow-wrap: break-word;
  word-break: normal;
}
</style>
